import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload, formatImageUrl } from '../../utils/security';
import { toast } from 'react-toastify';
import { Ticket, MessageSquare, Trash2, ExternalLink, RefreshCw, Send, Image as ImageIcon, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, Clock, Activity, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminTickets = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const [ticketDetails, setTicketDetails] = useState({});
    const [replyText, setReplyText] = useState('');
    const [replyAttach, setReplyAttach] = useState(null);
    const [replyAttachPreview, setReplyAttachPreview] = useState(null);
    const [isReplying, setIsReplying] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    const openLightbox = (imagesList, startIndex = 0) => {
        setLightboxImages(imagesList);
        setLightboxIndex(startIndex);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => setIsLightboxOpen(false);

    const prevLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
    }, [lightboxImages.length]);

    const nextLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
    }, [lightboxImages.length]);

    useEffect(() => {
        if (!isLightboxOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') prevLightboxImage();
            if (e.key === 'ArrowRight') nextLightboxImage();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen, prevLightboxImage, nextLightboxImage]);


    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}tickets.php?admin=1`);
            const data = await res.json();
            if (Array.isArray(data)) setTickets(data);
        } catch {
            toast.error(t('admin.tickets.toastLoadError') || 'ტიკეტების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user && isSuperAdmin()) fetchTickets();
    }, [user, isSuperAdmin, fetchTickets]);

    const fetchTicketDetails = async (id) => {
        try {
            const res = await fetchWithSecurity(`${API_URL}tickets.php?id=${id}`);
            const data = await res.json();
            setTicketDetails(prev => ({ ...prev, [id]: data }));
        } catch {
            toast.error(t('admin.tickets.toastDetailsError') || 'ტიკეტის დეტალების ჩატვირთვა ვერ მოხერხდა');
        }
    };

    const toggleExpand = (id) => {
        if (expandedId === id) {
            setExpandedId(null);
        } else {
            setExpandedId(id);
            if (!ticketDetails[id]) fetchTicketDetails(id);
        }
    };

    const handleStatusChange = async (ticketId, newStatus) => {
        try {
            const sanitized = sanitizePayload({ status: newStatus });
            const res = await fetchWithSecurity(`${API_URL}tickets.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_status', id: ticketId, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.tickets.toastStatusUpdated') || 'სტატუსი განახლდა');
                fetchTickets();
            } else {
                toast.error(data.error || (t('admin.tickets.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.tickets.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა');
        }
    };

    const deleteTicket = async (id) => {
        if (!window.confirm(t('admin.tickets.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ ტიკეტის წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}tickets.php?id=${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.tickets.toastDeleted') || 'ტიკეტი წაიშალა');
                setTickets(prev => prev.filter(t => t.id !== id));
            } else {
                toast.error(data.error || (t('admin.tickets.toastDeleteError') || 'წაშლა ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.tickets.toastDeleteError') || 'წაშლა ვერ მოხერხდა');
        }
    };

    const handleReplyAttach = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setReplyAttach(file);
        setReplyAttachPreview(URL.createObjectURL(file));
    };

    const submitReply = async (ticketId) => {
        if (!replyText.trim() && !replyAttach) return;
        setIsReplying(true);
        try {
            let attachmentUrl = '';
            if (replyAttach) {
                const formData = new FormData();
                formData.append('image', replyAttach);
                const upRes = await fetchWithSecurity(`${API_URL}upload.php`, { method: 'POST', body: formData });
                const upData = await upRes.json();
                if (upData.success) attachmentUrl = upData.url;
            }
            const sanitized = sanitizePayload({ message: replyText, attachment_url: attachmentUrl });
            const res = await fetchWithSecurity(`${API_URL}ticket_comments.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ticket_id: ticketId, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.tickets.toastReplySent') || 'პასუხი გაიგზავნა');
                setReplyText('');
                setReplyAttach(null);
                setReplyAttachPreview(null);
                fetchTicketDetails(ticketId);
                fetchTickets();
            } else {
                toast.error(data.error || (t('admin.tickets.toastReplyError') || 'პასუხის გაგზავნა ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.tickets.toastReplyError') || 'პასუხის გაგზავნა ვერ მოხერხდა');
        }
        setIsReplying(false);
    };

    if (authLoading || !user) return null;

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Open': return 'shadcn-badge-warning';
            case 'In Progress': return 'shadcn-badge-info';
            case 'Resolved': return 'shadcn-badge-success';
            case 'Closed': return 'shadcn-badge-danger';
            default: return 'shadcn-badge-primary';
        }
    };

    const getStatusCardStyle = (status) => {
        switch (status) {
            case 'Open':        return { border: '2px solid #eab308', background: 'rgba(234,179,8,0.04)' };
            case 'In Progress': return { border: '2px solid #3b82f6', background: 'rgba(59,130,246,0.04)' };
            case 'Resolved':    return { border: '2px solid #22c55e', background: 'rgba(34,197,94,0.04)' };
            case 'Closed':      return { border: '2px solid #64748b', background: 'rgba(100,116,139,0.04)' };
            default:            return { border: '2px solid #e2e8f0', background: '#fff' };
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Open':        return <Clock size={16} style={{ color: '#eab308' }} />;
            case 'In Progress': return <Activity size={16} style={{ color: '#3b82f6' }} />;
            case 'Resolved':    return <CheckCircle2 size={16} style={{ color: '#22c55e' }} />;
            case 'Closed':      return <XCircle size={16} style={{ color: '#64748b' }} />;
            default:            return <AlertCircle size={16} className="text-muted" />;
        }
    };

    const filteredTickets = filterStatus === 'all' ? tickets : tickets.filter(t => t.status === filterStatus);

    return (
        <AdminLayout title={t('admin.tickets.title') || 'ტიკეტები'}>
            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                    <div className="shadcn-card-title">
                        <Ticket size={20} className="text-indigo" />
                        <span>{t('admin.tickets.title') || 'ტიკეტები'} ({tickets.length})</span>
                    </div>
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchTickets}>
                        <RefreshCw size={14} /> {t('admin.tickets.refresh') || 'განახლება'}
                    </button>
                </div>

                {/* Filter Pills */}
                <div className="d-flex gap-2 mb-4 flex-wrap">
                    {['all', 'Open', 'In Progress', 'Resolved', 'Closed'].map(s => {
                        const labelMap = {
                            all: t('admin.tickets.filterAll') || 'ყველა ტიკეტი',
                            Open: t('admin.tickets.filterOpen') || 'ღია',
                            'In Progress': t('admin.tickets.filterInProgress') || 'მიმდინარე',
                            Resolved: t('admin.tickets.filterResolved') || 'გადაწყვეტილი',
                            Closed: t('admin.tickets.filterClosed') || 'დახურული'
                        };
                        return (
                            <button
                                key={s}
                                className={`shadcn-btn shadcn-btn-sm ${filterStatus === s ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setFilterStatus(s)}
                            >
                                <span>{labelMap[s] || s}</span>
                                {s !== 'all' && (
                                    <span className="ms-1 px-2 py-0.5 rounded-pill bg-slate-100 text-slate-700 fw-bold text-xs border">
                                        {tickets.filter(t => t.status === s).length}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('admin.tickets.loadingTickets') || 'ტიკეტები იტვირთება...'}</span>
                    </div>
                ) : filteredTickets.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">{t('admin.tickets.noTickets') || 'ამ სტატუსით ტიკეტები არ მოიძებნა.'}</p>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {filteredTickets.map(ticket => {
                            const detail = ticketDetails[ticket.id];
                            const isExpanded = expandedId === ticket.id;
                            return (
                                <div
                                    key={ticket.id}
                                    className={`rounded-3 overflow-hidden transition-all ${isExpanded ? 'shadow-sm' : ''}`}
                                    style={getStatusCardStyle(ticket.status)}
                                >
                                    {/* Header Row */}
                                    <div
                                        className="p-3 d-flex align-items-center justify-content-between cursor-pointer"
                                        onClick={() => toggleExpand(ticket.id)}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="p-2 rounded-3" style={{ background: 'rgba(99,102,241,0.08)' }}>
                                                {getStatusIcon(ticket.status)}
                                            </div>
                                            <div>
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <span className="text-muted small">#{ticket.id}</span>
                                                    <Link to={`/admin/tickets/detail/${ticket.id}`} className="fw-bold text-dark fs-6 text-decoration-none hover-primary">
                                                        {ticket.subject}
                                                    </Link>
                                                    {ticket.comment_count > 0 && (
                                                        <span className="shadcn-badge shadcn-badge-info">
                                                            <MessageSquare size={12} /> {ticket.comment_count}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="d-flex gap-3 text-muted smallest">
                                                    <span>{t('admin.tickets.colCategory') || 'კატეგორია:'} <strong className="text-dark">{ticket.category_name}</strong></span>
                                                    <span>{t('admin.tickets.colClient') || 'კლიენტი:'} <strong className="text-dark">{ticket.username}</strong></span>
                                                    <span>{new Date(ticket.created_at).toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                                            <select
                                                className={`shadcn-select form-select-sm py-1 ${getStatusBadgeClass(ticket.status)}`}
                                                value={ticket.status}
                                                onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                                            >
                                                <option value="Open">{t('admin.tickets.statusOpen') || 'ღია'}</option>
                                                <option value="In Progress">{t('admin.tickets.statusInProgress') || 'მიმდინარე'}</option>
                                                <option value="Resolved">{t('admin.tickets.statusResolved') || 'გადაწყვეტილი'}</option>
                                                <option value="Closed">{t('admin.tickets.statusClosed') || 'დახურული'}</option>
                                            </select>

                                            <Link to={`/admin/tickets/detail/${ticket.id}`} className="shadcn-btn shadcn-btn-sm shadcn-btn-primary">
                                                <ExternalLink size={14} /> {t('common.details') || 'დეტალები'}
                                            </Link>
                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteTicket(ticket.id)} title={t('admin.tickets.delete') || 'წაშლა'}>
                                                <Trash2 size={14} />
                                            </button>
                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => toggleExpand(ticket.id)}>
                                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Expanded Details & Discussion */}
                                    {isExpanded && (
                                        <div className="p-4 border-top bg-slate-50">
                                            <div className="p-3 rounded-3 bg-white border mb-4 shadow-sm">
                                                <div className="text-muted smallest mb-2 fw-semibold">{t('admin.tickets.originalMessage') || 'ორიგინალი შეტყობინება:'}</div>
                                                <p className="text-dark mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{ticket.message}</p>
                                                {ticket.attachment_url && (
                                                    <div className="mt-3">
                                                        <div 
                                                            className="cursor-pointer d-inline-block position-relative shadow-sm rounded-3 overflow-hidden border"
                                                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                            onClick={() => {
                                                                const list = [];
                                                                if (ticket.attachment_url) list.push({ url: formatImageUrl(ticket.attachment_url), title: `${ticket.subject || 'Ticket'} #${ticket.id}`, sender: ticket.username, date: ticket.created_at });
                                                                (detail?.comments || []).forEach((c, idx) => {
                                                                    if (c.attachment_url) list.push({ url: formatImageUrl(c.attachment_url), title: `${t('admin.tickets.replyLabel') || 'Reply'} #${idx + 1}`, sender: c.username, date: c.created_at });
                                                                });
                                                                openLightbox(list, 0);
                                                            }}
                                                            title="გადიდება (Lightbox)"
                                                        >
                                                            <img
                                                                src={formatImageUrl(ticket.attachment_url)}
                                                                alt="Attachment"
                                                                style={{ maxWidth: '200px', maxHeight: '130px', objectFit: 'cover', borderRadius: '8px', display: 'block' }}
                                                            />
                                                            <span className="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white smallest px-2 py-1 rounded-top-start">
                                                                🔍 Lightbox
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Replies List */}
                                            {!detail ? (
                                                <p className="text-muted small">{t('admin.tickets.loadingComments') || 'მიმოწერა იტვირთება...'}</p>
                                            ) : detail.comments?.length > 0 ? (
                                                <div className="d-flex flex-column gap-3 mb-4">
                                                    {detail.comments.map(c => (
                                                        <div
                                                            key={c.id}
                                                            className={`p-3 rounded-3 border ${c.role === 'super_admin' ? 'bg-primary bg-opacity-10 border-primary border-opacity-30' : 'bg-white shadow-sm'}`}
                                                        >
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span className="text-dark small fw-bold d-flex align-items-center gap-2">
                                                                    {c.username}
                                                                    {c.role === 'super_admin' && (
                                                                        <span className="shadcn-badge shadcn-badge-primary">{t('admin.tickets.badgeSupport') || 'მხარდაჭერის გუნდი'}</span>
                                                                    )}
                                                                </span>
                                                                <span className="text-muted smallest">{new Date(c.created_at).toLocaleString()}</span>
                                                            </div>
                                                            <p className="text-dark mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{c.message}</p>
                                                            {c.attachment_url && (
                                                                <div className="mt-2">
                                                                    <div 
                                                                        className="cursor-pointer d-inline-block position-relative shadow-sm rounded-2 overflow-hidden border"
                                                                        style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                        onClick={() => {
                                                                            const list = [];
                                                                            if (ticket.attachment_url) list.push({ url: formatImageUrl(ticket.attachment_url), title: `${ticket.subject || 'Ticket'} #${ticket.id}`, sender: ticket.username, date: ticket.created_at });
                                                                            (detail?.comments || []).forEach((item, idx) => {
                                                                                if (item.attachment_url) list.push({ url: formatImageUrl(item.attachment_url), title: `${t('admin.tickets.replyLabel') || 'Reply'} #${idx + 1}`, sender: item.username, date: item.created_at });
                                                                            });
                                                                            const targetUrl = formatImageUrl(c.attachment_url);
                                                                            const idx = list.findIndex(item => item.url === targetUrl);
                                                                            openLightbox(list, idx >= 0 ? idx : 0);
                                                                        }}
                                                                        title="გადიდება (Lightbox)"
                                                                    >
                                                                        <img src={formatImageUrl(c.attachment_url)} alt="Attachment" style={{ maxWidth: '170px', maxHeight: '110px', objectFit: 'cover', borderRadius: '6px', display: 'block' }} />
                                                                        <span className="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white smallest px-2 py-0 rounded-top-start">
                                                                            🔍
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-muted small mb-4">{t('admin.tickets.noReplies') || 'პასუხები ჯერ არ არის.'}</p>
                                            )}

                                            {/* Reply Form */}
                                            {ticket.status !== 'Closed' && (
                                                <div className="pt-3 border-top">
                                                    <textarea
                                                        className="shadcn-textarea mb-3"
                                                        rows="3"
                                                        placeholder={t('admin.tickets.replyPlaceholder') || 'დაწერეთ პასუხი...'}
                                                        value={replyText}
                                                        onChange={e => setReplyText(e.target.value)}
                                                    />
                                                    {replyAttachPreview && (
                                                        <div className="mb-3 d-flex align-items-center gap-2">
                                                            <img src={replyAttachPreview} alt="preview" style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px' }} />
                                                            <button type="button" className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => { setReplyAttach(null); setReplyAttachPreview(null); }}>
                                                                {t('common.remove') || 'წაშლა'}
                                                            </button>
                                                        </div>
                                                    )}
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <label className="shadcn-btn shadcn-btn-secondary cursor-pointer mb-0">
                                                            <ImageIcon size={16} /> {replyAttach ? replyAttach.name : (t('admin.tickets.attachImage') || 'ფაილის მიბმა')}
                                                            <input type="file" accept="image/*" onChange={handleReplyAttach} style={{ display: 'none' }} />
                                                        </label>
                                                        <button className="shadcn-btn shadcn-btn-primary" onClick={() => submitReply(ticket.id)} disabled={isReplying || (!replyText.trim() && !replyAttach)}>
                                                            <Send size={16} /> {isReplying ? (t('admin.tickets.sending') || 'იგზავნება...') : (t('admin.tickets.sendReply') || 'პასუხის გაგზავნა')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {/* ── LIGHTBOX MODAL WITH PREV/NEXT CONTROLS ── */}
            {isLightboxOpen && lightboxImages.length > 0 && (
                <div 
                    className="orbx-lightbox-overlay" 
                    onClick={closeLightbox}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(5, 8, 22, 0.94)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 999999,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '24px'
                    }}
                >
                    {/* Top Bar */}
                    <div 
                        className="d-flex justify-content-between align-items-center w-100"
                        style={{ maxWidth: '1200px', zIndex: 1000000 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="text-white">
                            <span className="fw-bold fs-6 d-block text-white">
                                {lightboxImages[lightboxIndex]?.title}
                            </span>
                            <span className="smallest text-white-50">
                                {lightboxImages[lightboxIndex]?.sender} • {lightboxImages[lightboxIndex]?.date ? new Date(lightboxImages[lightboxIndex].date).toLocaleString() : ''}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className="badge rounded-pill bg-white bg-opacity-10 text-white px-3 py-2 fw-semibold fs-7 border border-white border-opacity-10">
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </span>
                            <button
                                type="button"
                                onClick={closeLightbox}
                                className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '42px', height: '42px', border: '1px solid rgba(255,255,255,0.2)' }}
                                title={t('common.close') || 'Close'}
                            >
                                <X size={22} />
                            </button>
                        </div>
                    </div>

                    {/* Main Image Viewer */}
                    <div 
                        className="position-relative d-flex align-items-center justify-content-center flex-grow-1 w-100 my-3"
                        style={{ minHeight: 0 }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Prev Button */}
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={prevLightboxImage}
                                className="btn position-absolute start-0 top-50 translate-middle-y text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                style={{
                                    zIndex: 1000001,
                                    width: '54px',
                                    height: '54px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer'
                                }}
                                title={t('common.prev') || 'Previous'}
                            >
                                <ChevronLeft size={30} />
                            </button>
                        )}

                        <div className="d-flex align-items-center justify-content-center h-100 w-100 p-2">
                            <img
                                src={lightboxImages[lightboxIndex]?.url}
                                alt={lightboxImages[lightboxIndex]?.title || 'Attachment'}
                                style={{
                                    maxHeight: '78vh',
                                    maxWidth: '82vw',
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}
                            />
                        </div>

                        {/* Next Button */}
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={nextLightboxImage}
                                className="btn position-absolute end-0 top-50 translate-middle-y text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                style={{
                                    zIndex: 1000001,
                                    width: '54px',
                                    height: '54px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer'
                                }}
                                title={t('common.next') || 'Next'}
                            >
                                <ChevronRight size={30} />
                            </button>
                        )}
                    </div>

                    {/* Bottom Strip */}
                    {lightboxImages.length > 1 && (
                        <div 
                            className="d-flex gap-2 justify-content-center overflow-auto py-2 px-3 rounded-pill bg-black bg-opacity-40 border border-white border-opacity-10"
                            style={{ maxWidth: '90vw', zIndex: 1000000 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {lightboxImages.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img.url}
                                    alt={`thumb-${idx}`}
                                    onClick={() => setLightboxIndex(idx)}
                                    style={{
                                        width: '46px',
                                        height: '34px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        opacity: idx === lightboxIndex ? 1 : 0.45,
                                        border: idx === lightboxIndex ? '2px solid #6366f1' : '1px solid transparent',
                                        transform: idx === lightboxIndex ? 'scale(1.1)' : 'scale(1)',
                                        transition: 'all 0.2s ease'
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminTickets;
